import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Building2, Plus, Edit, RefreshCw, Loader2, Eye } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { bankingApi, BankAccount } from '../lib/api/banking';
import { BankAccountForm } from './bank-account-form';
import { TranslatedText } from './translated-text';
import { useTranslation } from '../hooks/useTranslation';

interface BankManagementProps {
  companyId?: string;
  onRefresh?: () => void;
}

export function BankManagement({ companyId, onRefresh }: BankManagementProps) {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
  const { toast } = useToast();
  const { tSync } = useTranslation();

  const fetchAccounts = async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch all accounts (including inactive/closed) to show the user what's in the database
      const data = await bankingApi.getBankAccounts(companyId);
      setAccounts(data || []);
      
      // Log for debugging
      console.log('📊 Bank Accounts Fetched:', {
        total: data?.length || 0,
        accounts: data?.map((a: any) => ({
          id: a.id,
          bankName: a.bankName,
          accountNumber: a.accountNumber,
          status: a.status
        })) || []
      })
    } catch (error: any) {
      console.error('Failed to fetch bank accounts:', error);
      toast({
        title: tSync("Error"),
        description: error.message || tSync("Failed to load bank accounts"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [companyId]);

  const handleCreate = () => {
    setEditingAccount(null);
    setIsFormOpen(true);
  };

  const handleEdit = (account: BankAccount) => {
    setEditingAccount(account);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingAccount(null);
    fetchAccounts();
    if (onRefresh) onRefresh();
  };

  const formatCurrency = (amount: number | string | undefined, currency: string = 'RWF') => {
    if (!amount) return `0.00 Frw`;
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
    return `${formatted} Frw`;
  };

  const formatAccountType = (type?: string) => {
    if (!type) return tSync('N/A');
    // Translate common account types
    const typeLower = type.toLowerCase();
    if (typeLower.includes('checking')) return tSync('Checking');
    if (typeLower.includes('savings')) return tSync('Savings');
    if (typeLower.includes('current')) return tSync('Current');
    // Fallback to formatted version
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600"><TranslatedText text="Loading bank accounts..." /></span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-semibold"><TranslatedText text="Bank Management" /></h2>
          <Badge variant="secondary" className="ml-2">
            {accounts.length} <TranslatedText text={accounts.length === 1 ? 'Account' : 'Accounts'} />
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAccounts}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            <TranslatedText text="Refresh" />
          </Button>
          <Button
            onClick={handleCreate}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <TranslatedText text="Add Bank Account" />
          </Button>
        </div>
      </div>


      {accounts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-12">
            <Building2 className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2"><TranslatedText text="No Bank Accounts" /></h3>
            <p className="text-sm text-gray-600 mb-4 text-center">
              <TranslatedText text="You haven't created any bank accounts yet. Create your first bank account to get started." />
            </p>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              <TranslatedText text="Create Bank Account" />
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle><TranslatedText text="Bank Accounts" /></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left p-3 font-semibold text-sm"><TranslatedText text="Bank Name" /></th>
                      <th className="text-left p-3 font-semibold text-sm"><TranslatedText text="Account Number" /></th>
                      <th className="text-left p-3 font-semibold text-sm"><TranslatedText text="Account Type" /></th>
                      <th className="text-left p-3 font-semibold text-sm"><TranslatedText text="Currency" /></th>
                      <th className="text-left p-3 font-semibold text-sm"><TranslatedText text="Balance" /></th>
                      <th className="text-left p-3 font-semibold text-sm"><TranslatedText text="Status" /></th>
                      <th className="text-left p-3 font-semibold text-sm"><TranslatedText text="Branch" /></th>
                      <th className="text-right p-3 font-semibold text-sm"><TranslatedText text="Actions" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr key={account.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 text-sm">
                          <div className="font-medium text-gray-900">{account.bankName}</div>
                          {account.swiftCode && (
                            <div className="text-xs text-gray-500">SWIFT: {account.swiftCode}</div>
                          )}
                        </td>
                        <td className="p-3 text-sm font-mono text-gray-700">
                          {account.accountNumber}
                        </td>
                        <td className="p-3 text-sm">
                          <Badge variant="outline">
                            {formatAccountType(account.accountType)}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {(() => {
                            const currency = account.currency || 'RWF';
                            return currency === 'USD' || currency === 'RWF' || currency === 'FRW' ? 'Frw' : currency;
                          })()}
                        </td>
                        <td className="p-3 text-sm font-semibold">
                          {formatCurrency(account.balance, account.currency)}
                        </td>
                        <td className="p-3 text-sm">
                          <Badge 
                            variant={account.status === 'active' ? 'default' : account.status === 'closed' ? 'destructive' : 'secondary'}
                          >
                            {tSync(account.status || 'active')}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {account.branchName || account.branchCode || tSync('N/A')}
                        </td>
                        <td className="p-3 text-sm">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(account)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bank Account Form Dialog */}
      <BankAccountForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSuccess={handleFormSuccess}
        companyId={companyId}
        editingAccount={editingAccount}
      />
    </div>
  );
}
